import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from 'service';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const SignUpForm = ({ onBackToLogin }) => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState('signup'); // 'signup' | 'otp'
  const [formData, setFormData] = useState({ email: '', password: '', type: "verify" });
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'At least 6 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const result = await authAPI.signUp(formData.email, formData.password);
    setLoading(false);

    if (!result.success) {
      if (result.error === 'An account with this email exist') {
        try {
          setLoading(true);
          const otpRes = await authAPI.requestOtp(formData.email, formData.type);
          setLoading(false);

          if (otpRes.success) {
            setStep('otp');
          } else {
            return setErrors({ submit: otpRes.error || 'Failed to request OTP' });
          }
        } catch (error) {
          setLoading(false);
          return setErrors({ submit: 'Something went wrong requesting OTP' });
        }
      } else {
        return setErrors({ submit: result.error });
      }
    } else {
      setErrors({});
      setStep('otp'); // move to OTP step
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 5) {
      setErrors({ otp: 'OTP must be 5 digits' });
      return;
    }

    setLoading(true);
    const res = await authAPI.verifyOtp(formData.email, otp, formData.type);
    setLoading(false);

    if (!res.success) {
      setErrors({ otp: res.error });
    } else {
      setErrors({});
      await login(formData.email, formData.password);
      navigate('/chapter-overview-dashboard');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <form onSubmit={step === 'signup' ? handleSignup : handleVerifyOtp} className="space-y-6">
      {step === 'signup' && (
        <>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Email</label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                disabled={loading}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  disabled={loading}
                  className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute text-gray-500 -translate-y-1/2 right-2 top-1/2"
                  disabled={loading}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 border border-red-200 rounded-md bg-red-50">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}
        </>
      )}

      {step === 'otp' && (
        <>
          <p className="text-sm text-gray-700">We sent a 5-digit OTP to <strong>{formData.email}</strong>.</p>
          <Input
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter OTP"
            maxLength={5}
            className={errors.otp ? 'border-red-500' : ''}
            disabled={loading}
          />
          {errors.otp && <p className="text-sm text-red-600">{errors.otp}</p>}
        </>
      )}

      <Button type="submit" fullWidth loading={loading} disabled={loading}>
        {step === 'signup' ? 'Sign Up' : 'Verify OTP'}
      </Button>

      <span className="block text-sm text-center text-gray-500">
        {step === 'otp' ? (
          <button type="button" onClick={() => setStep('signup')} className="text-primary hover:text-primary/80">
            Edit email or password
          </button>
        ) : (
          <>
            Already have an account?{' '}
            <button type="button" onClick={onBackToLogin} className="text-primary hover:text-primary/80">
              Back to Login
            </button>
          </>
        )}
      </span>
    </form>
  );
};

export default SignUpForm;
